import {
  FC,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  SyntheticEvent,
} from 'react';

import {
  U21ModalBase,
  U21ModalBaseProps,
} from 'app/shared/u21-ui/components/layout/modal/U21ModalBase';
import {
  U21ModalBody,
  U21ModalBodyProps,
} from 'app/shared/u21-ui/components/layout/modal/U21ModalBody';
import { U21ModalFooter } from 'app/shared/u21-ui/components/layout/modal/U21ModalFooter';
import { U21ModalHeader } from 'app/shared/u21-ui/components/layout/modal/U21ModalHeader';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  U21Button,
  U21ButtonProps,
} from 'app/shared/u21-ui/components/input/U21Button';
import {
  U21SplitButton,
  U21SplitButtonProps,
} from 'app/shared/u21-ui/components/input/U21SplitButton';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';

// Utils
import styled from 'styled-components';

export interface U21ModalProps extends Omit<
  U21ModalBaseProps,
  'onClose' | 'title'
> {
  actionButtonProps?: Partial<Omit<U21SplitButtonProps, 'onClick'>>;
  closeButtonProps?: Omit<U21ButtonProps, 'onClick'>;
  disableTitleCase?: boolean;
  footerLeftAction?: ReactNode;
  headerAction?: ReactNode;
  loading?: boolean;
  modalBodyProps?: Omit<U21ModalBodyProps, 'children'>;
  onAction?: MouseEventHandler<HTMLButtonElement>;
  onClose: (event: SyntheticEvent) => void;
  subtitle?: ReactNode;
  title?: ReactNode;
  titleIcon?: ReactElement;
  titleTooltip?: string;
}

export const U21Modal: FC<U21ModalProps> = ({
  actionButtonProps,
  children,
  closeButtonProps,
  disableTitleCase,
  footerLeftAction,
  headerAction,
  loading,
  modalBodyProps,
  onAction,
  onClose,
  onEnter,
  onExited,
  open,
  size,
  subtitle,
  title,
  titleIcon,
  titleTooltip,
  ...rest
}) => {
  return (
    <U21ModalBase
      onEnter={onEnter}
      onExited={onExited}
      open={open}
      size={size}
      {...rest}
    >
      <U21ModalHeader
        disableTitleCase={disableTitleCase}
        icon={titleIcon}
        onClose={onClose}
        tooltip={titleTooltip}
        subtitle={subtitle}
        action={headerAction}
      >
        {title}
      </U21ModalHeader>
      <StyledU21Loading loading={loading} />
      {!loading && (
        <U21ModalBody {...modalBodyProps}>
          {typeof children === 'string' ? (
            <U21Typography variant="subtitle2">{children}</U21Typography>
          ) : (
            children
          )}
        </U21ModalBody>
      )}
      <U21ModalFooter leftAction={footerLeftAction}>
        {/* eslint-disable-next-line react/no-children-prop */}
        <U21Button children="Cancel" {...closeButtonProps} onClick={onClose} />
        {(Boolean(onAction) || Boolean(actionButtonProps)) && (
          <U21SplitButton
            // eslint-disable-next-line react/no-children-prop
            children="Confirm"
            color="primary"
            items={[]}
            variant="contained"
            {...actionButtonProps}
            onClick={onAction}
          />
        )}
      </U21ModalFooter>
    </U21ModalBase>
  );
};

const StyledU21Loading = styled(U21Loading)`
  padding: 24px;
`;
